import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Input,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { ChatMessage, ChatService } from "../../services/chat.service";
import { SecureStorageService } from "../../services/secure-storage.service";

interface Emoji {
  code: string;
  emoji: string;
  name: string;
}

interface Sticker {
  id: string;
  url: string;
  name: string;
  category: string;
}

@Component({
  selector: "app-chat",
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="chat-sidebar">
      <div class="sidebar-header">
        <h3>Chat</h3>
        <span class="viewer-count-badge">{{ viewerCount }}</span>
      </div>

      <div class="chat-messages" #chatMessages>
        <div
          *ngFor="let msg of messages"
          class="message"
          [ngClass]="{ 'message-own': msg.user_id === currentUserId }"
          style="width: auto;"
        >
          <div class="message-avatar">
            <img
              [src]="
                msg.user.profile_picture || 'assets/images/avatar.jpeg'
              "
              [alt]="msg.user.name"
              class="avatar-image"
            />
          </div>
          <div class="message-content">
            <div class="message-header">
              <span class="message-username">{{ msg.user.prenom }}</span>
              <span class="message-time">{{
                msg.created_at | date : "shortTime"
              }}</span>
              <!-- Menu dots pour les actions -->
              <div class="message-actions" *ngIf="canModerateUser(msg)">
                <button
                  type="button"
                  class="dots-menu-btn"
                  (click)="toggleMessageMenu(msg.id, $event)"
                  [class.active]="activeMessageMenu === msg.id"
                >
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <!-- Menu dropdown -->
                <div
                  class="message-menu"
                  *ngIf="activeMessageMenu === msg.id"
                  #messageMenu
                >
                  <button
                    type="button"
                    class="menu-item danger"
                    (click)="removeParticipant(msg.user_id)"
                  >
                    <i class="fas fa-user-times"></i>
                    Retirer du live
                  </button>
                </div>
              </div>
            </div>
            <div class="message-text">
              <span
                *ngIf="!isSticker(msg)"
                [innerHTML]="formatMessageWithEmojis(msg.message)"
              ></span>
              <img
                *ngIf="isSticker(msg)"
                [src]="msg.message"
                [alt]="msg.sticker_name || 'Sticker'"
                class="message-sticker"
              />
            </div>
          </div>
        </div>

        <div *ngIf="messages.length === 0" class="no-messages">
          <i class="fas fa-comments"></i>
          <p>Aucun message pour le moment</p>
        </div>
      </div>

      <!-- Panneau des stickers et émoticônes -->
      <div class="emoji-sticker-panel" *ngIf="showEmojiPanel" #emojiPanel>
        <div class="panel-tabs">
          <button
            type="button"
            class="tab-btn"
            [class.active]="activeTab === 'emoji'"
            (mousedown)="$event.preventDefault()"
            (click)="setActiveTab('emoji')"
          >
            😀
          </button>
          <button
            type="button"
            class="tab-btn"
            [class.active]="activeTab === 'sticker'"
            (mousedown)="$event.preventDefault()"
            (click)="setActiveTab('sticker')"
          >
            🎭
          </button>
        </div>

        <div class="panel-content">
          <!-- Onglet Émoticônes -->
          <div *ngIf="activeTab === 'emoji'" class="emoji-grid">
            <button
              *ngFor="let emoji of emojis"
              class="emoji-btn"
              (click)="addEmoji(emoji.emoji)"
              [title]="emoji.name"
            >
              {{ emoji.emoji }}
            </button>
          </div>

          <!-- Onglet Stickers -->
          <div *ngIf="activeTab === 'sticker'" class="sticker-grid">
            <img
              *ngFor="let sticker of stickers"
              [src]="sticker.url"
              [alt]="sticker.name"
              class="sticker-btn"
              (click)="sendSticker(sticker)"
              [title]="sticker.name"
            />
          </div>
        </div>
      </div>

      <div class="chat-input-container" *ngIf="isChatEnabled">
        <form (ngSubmit)="sendMessage()" class="chat-form">
          <button
            type="button"
            class="emoji-toggle-btn"
            (click)="toggleEmojiPanel($event)"
            [class.active]="showEmojiPanel"
          >
            😀
          </button>
          <input
            type="text"
            [(ngModel)]="newMessage"
            name="message"
            class="chat-input"
            placeholder="Écrivez votre message..."
            [disabled]="!isChatEnabled"
            (blur)="onInputBlur()"
            (focus)="onInputFocus()"
            #chatInput
          />
          <button
            type="submit"
            class="send-btn"
            [disabled]="!newMessage.trim() || !isChatEnabled"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </form>
      </div>

      <div class="chat-disabled" *ngIf="!isChatEnabled">
        <i class="fas fa-comment-slash"></i>
        <p>Le chat est désactivé</p>
      </div>
    </div>
  `,
  styles: [
    `
      .chat-sidebar {
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        display: flex;
        flex-direction: column;
        overflow-y: scroll;
        height: 100%;
      }

      .sidebar-header {
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .sidebar-header h3 {
        margin: 0;
        font-size: 18px;
        color: #333;
      }

      .viewer-count-badge {
        background: #dc3545;
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 12px;
      }

      .chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 15px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      /* Style mobile - messages de bas en haut */
      @media (max-width: 768px) {
        .chat-sidebar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          height: auto;
          max-height: 60vh;
          border-radius: 12px 12px 0 0;
          display: flex;
          flex-direction: column;
        }

        .sidebar-header {
          flex-shrink: 0;
          border-bottom: none;
          border-top: 1px solid #eee;
          order: 1;
        }

        .chat-messages {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 10px;
          overflow-y: auto;
          min-height: 150px;
          max-height: calc(60vh - 140px);
          order: 2;
        }

        .chat-input-container {
          flex-shrink: 0;
          position: relative;
          bottom: auto;
          background: white;
          z-index: 10;
          order: 3;
          border-top: 1px solid #eee;
        }

        .emoji-sticker-panel {
          flex-shrink: 0;
          position: relative;
          bottom: auto;
          z-index: 9;
          order: 2;
          border-bottom: 1px solid #eee;
        }
      }

      .message {
        display: flex;
        gap: 10px;
        padding: 10px;
        border-radius: 8px;
        background-color: #f8f9fa;
      }

      .message-own {
        background-color: #e3f2fd;
        margin-left: auto;
        flex-direction: row-reverse;
      }

      .message-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
      }

      .avatar-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .message-content {
        flex: 1;
      }

      .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 5px;
        position: relative;
      }

      .message-username {
        font-weight: 500;
        font-size: 14px;
        color: #333;
      }

      .message-time {
        font-size: 12px;
        color: #666;
      }

      .message-actions {
        position: relative;
        margin-left: 8px;
      }

      .dots-menu-btn {
        background: none;
        border: none;
        color: #666;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        font-size: 12px;
        opacity: 0.7;
        transition: all 0.2s ease;
      }

      .dots-menu-btn:hover,
      .dots-menu-btn.active {
        background: #f0f0f0;
        opacity: 1;
      }

      .message-menu {
        position: absolute;
        top: 100%;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        min-width: 140px;
        overflow: hidden;
      }

      .menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 8px 12px;
        border: none;
        background: none;
        text-align: left;
        cursor: pointer;
        font-size: 13px;
        transition: background-color 0.2s;
      }

      .menu-item:hover {
        background: #f5f5f5;
      }

      .menu-item.danger {
        color: #dc3545;
      }

      .menu-item.danger:hover {
        background: #fff5f5;
      }

      .menu-item i {
        font-size: 12px;
        width: 14px;
      }

      .message-text {
        font-size: 14px;
        color: #333;
        word-break: break-word;
      }

      .message-sticker {
        max-width: 120px;
        max-height: 120px;
        border-radius: 8px;
      }

      .no-messages {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        color: #666;
        text-align: center;
      }

      .no-messages i {
        font-size: 24px;
        margin-bottom: 10px;
        color: #dc3545;
      }

      .no-messages p {
        margin: 0;
        font-size: 14px;
      }

      .emoji-sticker-panel {
        border-top: 1px solid #eee;
        background: white;
        max-height: 200px;
        display: flex;
        flex-direction: column;
      }

      .panel-tabs {
        display: flex;
        border-bottom: 1px solid #eee;
      }

      .tab-btn {
        flex: 1;
        padding: 10px;
        border: none;
        background: #f8f9fa;
        cursor: pointer;
        font-size: 1.2em;
        transition: background-color 0.2s;
      }

      .tab-btn.active {
        background: white;
        border-bottom: 2px solid #dc3545;
      }

      .panel-content {
        flex: 1;
        overflow-y: auto;
        padding: 10px;
      }

      .emoji-grid {
        display: grid;
        grid-template-columns: repeat(8, 1fr);
        gap: 5px;
      }

      .emoji-btn {
        background: none;
        border: none;
        font-size: 1.5em;
        padding: 5px;
        cursor: pointer;
        border-radius: 4px;
        transition: background-color 0.2s;
      }

      .emoji-btn:hover {
        background: #f8f9fa;
      }

      .sticker-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px;
      }

      .sticker-btn {
        width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 8px;
        cursor: pointer;
        transition: transform 0.2s;
      }

      .sticker-btn:hover {
        transform: scale(1.1);
      }

      .chat-input-container {
        padding: 15px;
        border-top: 1px solid #eee;
      }

      .chat-form {
        display: flex;
        gap: 8px;
        align-items: center;
        width: 100%;
      }

      .emoji-toggle-btn {
        background: none;
        border: none;
        font-size: 1.1em;
        padding: 6px;
        cursor: pointer;
        border-radius: 4px;
        transition: background-color 0.2s;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .emoji-toggle-btn:hover,
      .emoji-toggle-btn.active {
        background: #f8f9fa;
      }

      .chat-input {
        flex: 1;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 20px;
        font-size: 14px;
        outline: none;
        min-width: 0; /* Permet au flex de réduire la taille */
      }

      .chat-input:focus {
        border-color: #dc3545;
      }

      .send-btn {
        background: #dc3545;
        color: white;
        border: none;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        flex-shrink: 0;
      }

      .send-btn:hover:not(:disabled) {
        background: #c82333;
        transform: scale(1.05);
      }

      .send-btn:disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      .chat-disabled {
        padding: 20px;
        text-align: center;
        color: #666;
      }

      .chat-disabled i {
        font-size: 24px;
        margin-bottom: 10px;
        color: #dc3545;
      }

      .chat-disabled p {
        margin: 0;
        font-size: 14px;
      }

      @media (max-width: 768px) {
        .chat-sidebar {
          width: 100% !important;
        }

        .message {
          padding: 8px;
          font-size: 13px;
        }

        .message-avatar {
          width: 30px;
          height: 30px;
        }

        .message-username {
          font-size: 13px;
        }

        .message-text {
          font-size: 13px;
        }

        .message-time {
          font-size: 11px;
        }

        .chat-input {
          font-size: 13px;
          padding: 6px 10px;
        }

        .send-btn {
          width: 32px;
          height: 32px;
        }

        .emoji-toggle-btn {
          width: 28px;
          height: 28px;
          font-size: 1em;
        }
      }
    `,
  ],
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("chatMessages") chatMessages!: ElementRef;
  @ViewChild("emojiPanel") emojiPanel!: ElementRef;
  @ViewChild("chatInput") chatInput!: ElementRef;
  @Input() streamId!: string;
  @Input() viewerCount: number = 0;
  @Input() currentUserId: number = 0;

  messages: ChatMessage[] = [];
  newMessage: string = "";
  isChatEnabled: boolean = true;
  showEmojiPanel: boolean = false;
  activeTab: "emoji" | "sticker" = "emoji";
  emojis: Emoji[] = [];
  stickers: Sticker[] = [];
  activeMessageMenu: number | null = null;
  private subscriptions: Subscription[] = [];
  private currentUserProfilePicture: string = "";
  private currentUserPrenom: string = "Vous";

  constructor(
    private chatService: ChatService,
    private secureStorage: SecureStorageService
  ) {
    // Récupérer les infos de l'utilisateur depuis le localStorage décodé
    try {
      const profilePicture =
        this.secureStorage.getItem<string>("profile_picture");
      const prenom = this.secureStorage.getItem<string>("prenom");

      if (profilePicture) {
        this.currentUserProfilePicture = profilePicture;
      }
      if (prenom) {
        this.currentUserPrenom = prenom;
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des infos utilisateur:",
        error
      );
    }
  }

  ngOnInit(): void {
    this.loadChatMessages();
    this.loadEmojis();
    this.loadStickers();

    // S'abonner aux nouveaux messages
    this.subscriptions.push(
      this.chatService.onNewMessage().subscribe((message) => {
        if (message.stream_id === this.streamId) {
          console.log("📨 Nouveau message reçu:", message);
          console.log("📨 Type du message:", message.type);
          console.log("📨 Est un sticker?", this.isSticker(message));

          // Chercher un message optimiste correspondant (même contenu, même utilisateur, statut "sending")
          const optimisticIndex = this.messages.findIndex(
            (m) =>
              m.status === "sending" &&
              m.message === message.message &&
              m.user_id === message.user_id &&
              m.type === message.type
          );

          if (optimisticIndex !== -1) {
            // Remplacer le message optimiste par le vrai message du serveur
            this.messages[optimisticIndex] = message;
          } else {
            // Vérifier si le message existe déjà (par ID)
            const existingIndex = this.messages.findIndex(
              (m) => m.id && message.id && m.id === message.id
            );

            if (existingIndex === -1) {
              // Nouveau message, l'ajouter
              this.messages.push(message);
            } else {
              // Message existant, le mettre à jour
              this.messages[existingIndex] = message;
            }
          }
          this.scrollToBottom();
        }
      })
    );

    // S'abonner à l'état du chat
    this.subscriptions.push(
      this.chatService.getChatState().subscribe((state) => {
        this.isChatEnabled = state.enabled;
      })
    );

    // S'abonner au canal de chat du stream
    this.chatService.subscribeToStreamChat(this.streamId);
  }

  private loadChatMessages() {
    this.chatService.getMessages(this.streamId).subscribe({
      next: (messages: any) => {
        this.messages = messages.data;
        this.scrollToBottom();
      },
      error: (error) => {
        console.error("Erreur lors du chargement des messages:", error);
      },
    });
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || !this.isChatEnabled) return;

    const messageText = this.newMessage;

    // Créer un message optimiste pour affichage immédiat
    const optimisticMessage: ChatMessage = {
      id: Date.now(), // ID temporaire
      message: messageText,
      type: "text",
      user_id: this.currentUserId,
      stream_id: this.streamId,
      created_at: new Date().toISOString(),
      status: "sending",
      user: {
        name: this.currentUserPrenom,
        prenom: this.currentUserPrenom,
        profile_picture: this.currentUserProfilePicture,
      },
    };

    // Ajouter immédiatement le message à la liste
    this.messages.push(optimisticMessage);
    this.scrollToBottom();

    // Vider le champ immédiatement pour une meilleure UX
    this.newMessage = "";
    this.showEmojiPanel = false;

    // Envoyer au serveur en arrière-plan
    this.chatService
      .sendMessage(this.streamId, messageText, "text")
      .then((response) => {
        // Le message sera mis à jour via WebSocket
      })
      .catch((error) => {
        console.error("Erreur lors de l'envoi du message:", error);
        // Optionnel : marquer le message comme échoué
        const msgIndex = this.messages.findIndex(
          (m) => m.id === optimisticMessage.id
        );
        if (msgIndex !== -1) {
          this.messages[msgIndex].status = "error";
        }
      });
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.chatMessages) {
        const element = this.chatMessages.nativeElement;
        // Toujours scroller vers le bas pour voir les nouveaux messages
        element.scrollTop = element.scrollHeight;
      }
    }, 100);
  }

  // Méthodes pour les émoticônes et stickers
  private loadEmojis(): void {
    this.emojis = [
      { code: ":smile:", emoji: "😀", name: "Sourire" },
      { code: ":grin:", emoji: "😁", name: "Grand sourire" },
      { code: ":joy:", emoji: "😂", name: "Pleurs de joie" },
      { code: ":heart_eyes:", emoji: "😍", name: "Yeux en cœur" },
      { code: ":wink:", emoji: "😉", name: "Clin d'œil" },
      { code: ":thumbsup:", emoji: "👍", name: "Pouce en l'air" },
      { code: ":thumbsdown:", emoji: "👎", name: "Pouce en bas" },
      { code: ":clap:", emoji: "👏", name: "Applaudissements" },
      { code: ":fire:", emoji: "🔥", name: "Feu" },
      { code: ":heart:", emoji: "❤️", name: "Cœur rouge" },
      { code: ":broken_heart:", emoji: "💔", name: "Cœur brisé" },
      { code: ":star:", emoji: "⭐", name: "Étoile" },
      { code: ":thinking:", emoji: "🤔", name: "Réflexion" },
      { code: ":cry:", emoji: "😢", name: "Pleurs" },
      { code: ":angry:", emoji: "😠", name: "Colère" },
      { code: ":surprised:", emoji: "😲", name: "Surprise" },
      { code: ":cool:", emoji: "😎", name: "Cool" },
      { code: ":kiss:", emoji: "😘", name: "Bisou" },
      { code: ":party:", emoji: "🎉", name: "Fête" },
      { code: ":muscle:", emoji: "💪", name: "Muscle" },
      { code: ":ok_hand:", emoji: "👌", name: "OK" },
      { code: ":peace:", emoji: "✌️", name: "Paix" },
      { code: ":pray:", emoji: "🙏", name: "Prière" },
      { code: ":wave:", emoji: "👋", name: "Salut" },
    ];
  }

  private loadStickers(): void {
    // Stickers par défaut - en production, ces données viendraient de l'API
    this.stickers = [
      {
        id: "1",
        url: "assets/stickers/happy.svg",
        name: "Heureux",
        category: "emotions",
      },
      {
        id: "2",
        url: "assets/stickers/love.svg",
        name: "Amour",
        category: "emotions",
      },
      {
        id: "3",
        url: "assets/stickers/cool.svg",
        name: "Cool",
        category: "emotions",
      },
      {
        id: "4",
        url: "assets/stickers/party.svg",
        name: "Fête",
        category: "celebrations",
      },
      {
        id: "5",
        url: "assets/stickers/thumbs-up.svg",
        name: "Pouce levé",
        category: "reactions",
      },
      {
        id: "6",
        url: "assets/stickers/clap.svg",
        name: "Applaudissements",
        category: "reactions",
      },
    ];
  }

  toggleEmojiPanel(event?: Event): void {
    // Empêcher la propagation du clic pour éviter le conflit avec clickListener
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.showEmojiPanel = !this.showEmojiPanel;

    // Si on ouvre le panneau, remettre le focus sur l'input
    if (this.showEmojiPanel && this.chatInput) {
      setTimeout(() => {
        this.chatInput.nativeElement.focus();
      }, 100);
    }
  }

  setActiveTab(tab: "emoji" | "sticker"): void {
    this.activeTab = tab;

    // Remettre le focus sur l'input après changement d'onglet
    if (this.chatInput) {
      setTimeout(() => {
        this.chatInput.nativeElement.focus();
      }, 100);
    }
  }

  addEmoji(emoji: string): void {
    this.newMessage += emoji;
    // Optionnel : fermer le panneau après sélection
    // this.showEmojiPanel = false;
  }

  sendSticker(sticker: Sticker): void {
    if (!this.isChatEnabled) return;

    // Créer un message optimiste pour le sticker
    const optimisticMessage: ChatMessage = {
      id: Date.now(), // ID temporaire
      message: sticker.url,
      type: "sticker",
      user_id: this.currentUserId,
      stream_id: this.streamId,
      created_at: new Date().toISOString(),
      status: "sending",
      sticker_name: sticker.name,
      user: {
        name: this.currentUserPrenom,
        prenom: this.currentUserPrenom,
        profile_picture: this.currentUserProfilePicture,
      },
    };

    // Ajouter immédiatement le sticker à la liste
    this.messages.push(optimisticMessage);
    this.scrollToBottom();
    this.showEmojiPanel = false;

    // Envoyer au serveur en arrière-plan
    this.chatService
      .sendMessage(this.streamId, sticker.url, "sticker", {
        name: sticker.name,
        id: sticker.id,
      })
      .then(() => {
        // Le message sera mis à jour via WebSocket
      })
      .catch((error) => {
        console.error("Erreur lors de l'envoi du sticker:", error);
        // Optionnel : marquer le message comme échoué
        const msgIndex = this.messages.findIndex(
          (m) => m.id === optimisticMessage.id
        );
        if (msgIndex !== -1) {
          this.messages[msgIndex].status = "error";
        }
      });
  }

  isSticker(message: any): boolean {
    // Vérifier si c'est un sticker basé sur le type ou l'URL
    return (
      message.type === "sticker" ||
      (message.message && message.message.includes("assets/stickers/")) ||
      (message.message && message.message.endsWith(".svg"))
    );
  }

  formatMessageWithEmojis(message: string): string {
    if (!message) return "";

    let formattedMessage = message;

    // Remplacer les codes d'émoticônes par les emojis
    this.emojis.forEach((emoji) => {
      const regex = new RegExp(
        emoji.code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g"
      );
      formattedMessage = formattedMessage.replace(regex, emoji.emoji);
    });

    return formattedMessage;
  }

  // Gestion du blur et focus
  onInputBlur(): void {
    // Délai pour permettre le clic sur les émoticônes/stickers
    setTimeout(() => {
      if (!this.isInteractingWithPanel) {
        this.showEmojiPanel = false;
      }
    }, 200);
  }

  onInputFocus(): void {
    // Optionnel : réouvrir le panneau si il était ouvert avant
    // this.showEmojiPanel = this.wasPanelOpen;
  }

  private isInteractingWithPanel: boolean = false;

  // Gestionnaire de clic global pour fermer le panneau
  private clickListener = (event: Event) => {
    const target = event.target as HTMLElement;
    const chatContainer = target.closest(".chat-sidebar");

    // Fermer le menu des messages si on clique ailleurs
    if (this.activeMessageMenu !== null) {
      if (!target.closest(".message-actions")) {
        this.activeMessageMenu = null;
      }
    }

    // Gestion du panneau d'emoji
    if (!this.showEmojiPanel) return;

    // Si le clic est complètement en dehors du composant chat, fermer le panneau
    if (!chatContainer) {
      this.showEmojiPanel = false;
      return;
    }

    // Si le clic est sur le panneau d'emoji ou ses enfants, ne pas fermer
    if (this.emojiPanel && this.emojiPanel.nativeElement.contains(target)) {
      return;
    }

    // Si le clic est sur l'input ou le bouton emoji, ne pas fermer
    if (target.closest(".chat-input") || target.closest(".emoji-toggle-btn")) {
      return;
    }

    // Sinon, fermer le panneau
    this.showEmojiPanel = false;
  };

  ngAfterViewInit(): void {
    // Ajouter le listener de clic global
    document.addEventListener("click", this.clickListener);

    // Ajouter les listeners pour détecter l'interaction avec le panneau
    if (this.emojiPanel) {
      const panelElement = this.emojiPanel.nativeElement;

      // Listeners pour le survol
      panelElement.addEventListener("mouseenter", () => {
        this.isInteractingWithPanel = true;
      });

      panelElement.addEventListener("mouseleave", () => {
        this.isInteractingWithPanel = false;
      });
    }
  }

  // Méthodes pour le menu des messages
  canModerateUser(message: any): boolean {
    // Vérifier si l'utilisateur actuel peut modérer (par exemple, s'il est le broadcaster)
    // Pour l'instant, on permet à tous les utilisateurs connectés de modérer
    // En production, il faudrait vérifier les permissions
    return this.currentUserId > 0 && message.user_id !== this.currentUserId;
  }

  toggleMessageMenu(messageId: number, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.activeMessageMenu === messageId) {
      this.activeMessageMenu = null;
    } else {
      this.activeMessageMenu = messageId;
    }
  }

  removeParticipant(userId: number): void {
    if (!userId || !this.streamId) {
      console.error("ID utilisateur ou slug du stream manquant");
      return;
    }

    const payload = {
      slug: this.streamId,
      user_id: Number(userId),
    };

    this.chatService.removeParticipant(payload).subscribe({
      next: (response) => {
        console.log("Participant retiré avec succès:", response);
        this.activeMessageMenu = null;
        // Optionnel : afficher une notification de succès
      },
      error: (error) => {
        console.error("Erreur lors du retrait du participant:", error);
        this.activeMessageMenu = null;
        // Optionnel : afficher une notification d'erreur
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.chatService.unsubscribeFromStreamChat(this.streamId);

    // Nettoyer le listener de clic global
    document.removeEventListener("click", this.clickListener);
  }
}
